import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Link2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Avatar } from "@/pages/admin/saker/Avatar";
import { SSTATUS, fmtDato } from "./strategiUtils";
import StrategyDrawer from "./StrategyDrawer";
import NyStrategiModal from "./NyStrategiModal";
import "./strategi.css";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");

const StatusPill = ({ status }) => {
  const s = SSTATUS[status] || SSTATUS.direction;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10.5px] font-medium shrink-0" style={{ color: s.dot }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
      {s.label}
    </span>
  );
};

const KortFot = ({ c, eier }) => (
  <div className="mt-auto pt-3 flex items-center gap-2.5 text-[10.5px] text-neutral-400">
    {eier && <Avatar person={eier} size={17} />}
    {(c.linkedEntities || []).length > 0 && (
      <span className="inline-flex items-center gap-1"><Link2 size={10.5} /> {c.linkedEntities.length}</span>
    )}
    {c.openTasks > 0 && <span>{c.openTasks} {c.openTasks === 1 ? "sak" : "saker"}</span>}
    <span className="ml-auto">{fmtDato(c.updatedAt)}</span>
  </div>
);

const KortRute = ({ c, eier, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="h-full w-full border-t border-neutral-300 pt-4 text-left flex flex-col gap-2.5 group"
    data-testid={`strategy-kort-${c.id}`}
  >
    <div className="flex items-center justify-between gap-3">
      <p className="strat-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-400 truncate">{c.title}</p>
      <StatusPill status={c.status} />
    </div>
    <p className="strat-statement text-[14.5px] leading-[1.5] text-neutral-800 font-medium group-hover:underline underline-offset-4 decoration-neutral-300">{c.statement}</p>
    {c.note && <p className="text-[11.5px] leading-snug text-neutral-400">{c.note}</p>}
    <KortFot c={c} eier={eier} />
  </button>
);

const AapentKort = ({ c, eier, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="h-full w-full border-t pt-4 text-left flex flex-col gap-2.5 group"
    style={{ borderTopColor: "#F59E0B" }}
    data-testid={`strategy-kort-${c.id}`}
  >
    <div className="flex items-center justify-between gap-3">
      <p className="strat-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500 truncate">{c.title}</p>
      <StatusPill status={c.status} />
    </div>
    <p className="strat-statement text-[15.5px] leading-[1.45] text-neutral-800 font-medium group-hover:underline underline-offset-4 decoration-neutral-300">{c.statement}</p>
    {(c.options || []).length > 0 && (
      <p className="strat-mono text-[10.5px] tracking-[0.08em] text-neutral-500 leading-relaxed">
        {c.options.join("  ·  ")}
      </p>
    )}
    {c.principle && (
      <p className="text-[11.5px] leading-snug text-neutral-500">
        <span className="strat-mono uppercase text-[9px] tracking-[0.14em] text-neutral-400">Decision principle · </span>
        {c.principle}
      </p>
    )}
    <KortFot c={c} eier={eier} />
  </button>
);

export default function StrategyBoard() {
  const [data, setData] = useState(null);
  const [aapenId, setAapenId] = useState(null);
  const [nyModal, setNyModal] = useState(false);

  const last = useCallback(async () => {
    try {
      const r = await api.get("/admin/os/strategy");
      setData(r.data);
    } catch (e) { feil(e); }
  }, []);
  useEffect(() => { last(); }, [last]);

  const members = data?.members || [];
  const byMember = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m])), [members]);

  const kort = useMemo(() => (data?.cards || []).filter((c) => !c.archived), [data]);
  const northstars = kort.filter((c) => c.pinned);
  const apne = kort.filter((c) => !c.pinned && c.status === "open");
  const grid = kort.filter((c) => !c.pinned && c.status !== "open");
  const aapen = kort.find((c) => c.id === aapenId) || null;

  const settKort = (card) =>
    setData((d) => ({ ...d, cards: d.cards.map((c) => (c.id === card.id ? { ...c, ...card, openTasks: card.openTasks ?? c.openTasks } : c)) }));

  const patch = async (cid, body) => {
    try {
      const r = await api.patch(`/admin/os/strategy/${cid}`, body);
      settKort(r.data.card);
    } catch (e) { feil(e); }
  };

  const opprett = async (form) => {
    try {
      const r = await api.post("/admin/os/strategy", form);
      setData((d) => ({ ...d, cards: [...d.cards, r.data.card] }));
      setNyModal(false);
      setAapenId(r.data.card.id);
    } catch (e) { feil(e); }
  };

  const slett = async (cid) => {
    try {
      await api.delete(`/admin/os/strategy/${cid}`);
      setData((d) => ({ ...d, cards: d.cards.filter((c) => c.id !== cid) }));
      setAapenId(null);
      toast.success("Kortet er slettet");
    } catch (e) { feil(e); }
  };

  const lagSak = async (cid) => {
    try {
      const r = await api.post(`/admin/os/strategy/${cid}/create-task`, {});
      settKort({ ...r.data.card, openTasks: (kort.find((c) => c.id === cid)?.openTasks || 0) + 1 });
      toast.success(`Sak opprettet i Saker: «${r.data.task.title}»`);
    } catch (e) { feil(e); }
  };

  if (!data) {
    return <p className="adm-sub" data-testid="strategy-laster">Laster strategy board …</p>;
  }

  return (
    <div data-testid="strategy-board">
      <div className="flex items-end justify-between gap-4 flex-wrap strat-rise">
        <div>
          <p className="adm-eyebrow">Strategy Board · {data.event?.name || "Norwegian AI Summit 2027"}</p>
          <h1 className="adm-title mt-1.5">Retning &amp; strategiske valg</h1>
          <p className="adm-sub mt-1.5 max-w-xl">
            What are we building, who is it for, how do we win — and what have we decided?
          </p>
        </div>
        <button type="button" onClick={() => setNyModal(true)} className="adm-btn inline-flex items-center gap-1.5" data-testid="ny-strategi-btn">
          <Plus size={13} /> Nytt kort
        </button>
      </div>

      {northstars.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => setAapenId(c.id)}
          className="strat-northstar strat-rise-2 mt-8 w-full text-left border-y border-[#E7E4DB] hover:border-neutral-300 py-7 sm:py-9 block"
          data-testid="strategy-northstar"
        >
          <div className="flex items-center gap-3">
            <p className="strat-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-400">{c.title}</p>
            <StatusPill status={c.status} />
          </div>
          <p className="strat-statement mt-3 text-[24px] sm:text-[34px] leading-[1.18] font-medium text-neutral-900 max-w-4xl">
            {c.statement}
          </p>
        </button>
      ))}

      {grid.length > 0 && (
        <div className="strat-rise-2 mt-10 grid gap-x-10 gap-y-10 sm:grid-cols-2 xl:grid-cols-3" data-testid="strategy-grid">
          {grid.map((c) => (
            <KortRute key={c.id} c={c} eier={byMember[c.ownerId]} onOpen={() => setAapenId(c.id)} />
          ))}
        </div>
      )}

      <section className="strat-rise-3 mt-10" data-testid="strategy-open-seksjon">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#F59E0B" }} />
          <p className="strat-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-600">
            Open decisions{apne.length > 0 ? ` — ${apne.length}` : ""}
          </p>
        </div>
        {apne.length === 0 ? (
          <p className="adm-sub mt-3">Ingen åpne beslutninger akkurat nå.</p>
        ) : (
          <div className="mt-5 grid gap-x-10 gap-y-9 lg:grid-cols-2">
            {apne.map((c) => (
              <AapentKort key={c.id} c={c} eier={byMember[c.ownerId]} onOpen={() => setAapenId(c.id)} />
            ))}
          </div>
        )}
      </section>

      {aapen && (
        <StrategyDrawer
          card={aapen}
          members={members}
          linkables={data.linkables || []}
          onPatch={(body) => patch(aapen.id, body)}
          onCreateTask={() => lagSak(aapen.id)}
          onDelete={() => slett(aapen.id)}
          onLukk={() => setAapenId(null)}
        />
      )}

      {nyModal && <NyStrategiModal onLagre={opprett} onLukk={() => setNyModal(false)} />}
    </div>
  );
}
